import { Minus, TrendingDown, TrendingUp, UserCheck, UserX } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fmtNumber } from '@/shared/lib/format'
import { RISK_LABEL, type UnitOverlay, type UnitResponse } from '@/shared/api/types'

/**
 * The four intelligence columns: turnout, mood, risk and agent.
 *
 * These render from `UnitResponse.overlay`. Every cell has one job before any
 * other: make "not assessed" visually distinct from "assessed as fine". The
 * legacy schema defaulted `riskLevel` to `LOW`, so an unassessed ward looked
 * identical to a safe one; that is the single most misleading thing it did, and
 * these cells exist partly to not repeat it.
 *
 * "—" in muted grey means nobody has looked. It never means zero, and it never
 * means low.
 */

/**
 * Nothing has been *assessed* for this unit.
 *
 * Agent coverage is deliberately not part of the test: it is derived by the server
 * from the stations beneath a unit, so "0 of 430 covered" is a measurement, not an
 * assessment, and a row that carries only coverage still means nobody has looked.
 * (It is also why an overlay row can exist with every assessment field null.)
 */
export function isUnassessed(o: UnitOverlay | null | undefined): boolean {
  if (!o) return true
  return o.turnoutPct == null && o.moodIndex == null && o.riskLevel == null
      && o.agentUserId == null
}

function Blank({ title }: { title?: string }) {
  return <span className="text-muted-foreground/60" title={title}>—</span>
}

// ── Turnout ─────────────────────────────────────────────────────────────────

export function TurnoutCell({ overlay }: { overlay?: UnitOverlay | null }) {
  const pct = overlay?.turnoutPct
  if (pct == null) return <Blank title="No turnout recorded" />

  // A percentage is a level, not a target, so the bar is neutral chocolate — no
  // red/green scale. "Good turnout" is a judgement this screen does not get to make.
  const width = Math.max(0, Math.min(100, pct))
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className="erp-metric tabular-nums">{pct.toFixed(1)}%</span>
      <span className="hidden 2xl:inline-block h-1.5 w-10 rounded-full bg-muted overflow-hidden align-middle">
        <span className="block h-full bg-chocolate" style={{ width: `${width}%` }} />
      </span>
      {overlay?.turnoutYear ? (
        <span
          className="text-erp-xs text-muted-foreground axis-hide-lg"
          title={overlay.turnoutBasis === 'DERIVED'
            ? `Derived from units beneath this one (${overlay.turnoutYear})`
            : `This unit's own ${overlay.turnoutYear} figure`}
        >
          {overlay.turnoutBasis === 'DERIVED' ? '≈' : ''}{overlay.turnoutYear}
        </span>
      ) : null}
    </span>
  )
}

// ── Mood ────────────────────────────────────────────────────────────────────

const TREND_ICON = {
  RISING: TrendingUp,
  FALLING: TrendingDown,
  STABLE: Minus,
} as const

export function MoodCell({ overlay }: { overlay?: UnitOverlay | null }) {
  const index = overlay?.moodIndex
  if (index == null) return <Blank title="Not assessed" />

  const trend = overlay?.moodTrend
  const Icon = trend && trend in TREND_ICON
    ? TREND_ICON[trend as keyof typeof TREND_ICON]
    : null

  // 0–100 favourability. 50 is the neutral midpoint, so the colour reads from
  // distance to 50 rather than from an arbitrary threshold.
  const tone = index >= 60 ? 'text-emerald-600 dark:text-emerald-400'
    : index <= 40 ? 'text-red-600 dark:text-red-400'
    : 'text-foreground'

  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap"
          title={overlay?.moodAssessedAt ? `Assessed ${overlay.moodAssessedAt}` : undefined}>
      <span className={cn('erp-metric tabular-nums', tone)}>{Math.round(index)}</span>
      {Icon && (
        <Icon className={cn(
          'h-3 w-3',
          trend === 'RISING' ? 'text-emerald-600 dark:text-emerald-400'
            : trend === 'FALLING' ? 'text-red-600 dark:text-red-400'
            : 'text-muted-foreground',
        )} aria-label={trend ?? undefined} />
      )}
    </span>
  )
}

// ── Risk ────────────────────────────────────────────────────────────────────

const RISK_STYLE: Record<string, string> = {
  LOW: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
  MEDIUM: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
  HIGH: 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/30',
  CRITICAL: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30',
}

export function RiskBadge({ level }: { level?: string | null }) {
  if (!level || level === 'UNKNOWN') return <Blank title="Not assessed" />
  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 text-erp-xs font-medium border rounded-md',
      RISK_STYLE[level] ?? 'bg-muted text-muted-foreground border-border',
    )}>
      {RISK_LABEL[level] ?? level}
    </span>
  )
}

// ── Agent ───────────────────────────────────────────────────────────────────

/**
 * A station has *an agent* — a person. Everything above a station has *coverage* —
 * a ratio. Rendering both as "agent" is how the column stops being trustworthy,
 * so they are visibly different shapes.
 */
export function AgentCell({ unit }: { unit: UnitResponse }) {
  const o = unit.overlay
  if (unit.unitType === 'POLLING_STATION') {
    if (!o?.agentUserId && !o?.agentName) {
      return (
        <span className="inline-flex items-center gap-1.5 text-muted-foreground/60"
              title="No agent assigned">
          <UserX className="h-3 w-3" /> —
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1.5 min-w-0"
            title={o.agentPhone ? `${o.agentName ?? 'Agent'} · ${o.agentPhone}` : undefined}>
        <UserCheck className={cn(
          'h-3 w-3 shrink-0',
          o.agentCheckedInAt ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground',
        )} />
        <span className="truncate">{o.agentName ?? 'Assigned'}</span>
      </span>
    )
  }

  const total = o?.stationsTotal ?? 0
  const covered = o?.stationsWithAgent ?? 0
  if (!total) return <Blank title="No coverage figures" />

  const pct = Math.round((covered / total) * 100)
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap"
          title={`${fmtNumber(covered)} of ${fmtNumber(total)} stations have an agent`}>
      <span className="erp-metric tabular-nums">{fmtNumber(covered)}/{fmtNumber(total)}</span>
      <span className={cn(
        'text-erp-xs',
        pct >= 90 ? 'text-emerald-600 dark:text-emerald-400'
          : pct >= 60 ? 'text-amber-600 dark:text-amber-400'
          : 'text-red-600 dark:text-red-400',
      )}>
        {pct}%
      </span>
    </span>
  )
}
